<script setup>
import { computed, onUnmounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { useConversationPersonalPreferences } from 'dashboard/composables/useConversationPersonalPreferences';
import { isTeamQuietConversation } from 'shared/helpers/conversationPersonalPreferences';
import { isLegacyQueueRescueConversation } from 'shared/helpers/legacyQueue';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import MergeConversationModal from './MergeConversationModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showMergeModal, toggleMergeModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const currentChatId = computed(() => currentChat.value.id);
const isTeamQuiet = computed(() => isTeamQuietConversation(currentChat.value));
const isRescueConversation = computed(() =>
  isLegacyQueueRescueConversation(currentChat.value)
);
const { isPinned, isQuiet, togglePinned, toggleQuiet } =
  useConversationPersonalPreferences(currentChatId);

const actionMenuItems = computed(() => {
  const items = [
    {
      icon: isPinned.value ? 'i-lucide-pin-off' : 'i-lucide-pin',
      label: t(
        isPinned.value
          ? 'CONVERSATION.PERSONAL_PREFERENCES.UNPIN'
          : 'CONVERSATION.PERSONAL_PREFERENCES.PIN'
      ),
      action: 'toggle_personal_pin',
      value: 'toggle_personal_pin',
    },
  ];

  if (!isRescueConversation.value || isQuiet.value) {
    items.push({
      icon: isQuiet.value ? 'i-lucide-bell' : 'i-lucide-bell-off',
      label: t(
        isQuiet.value
          ? 'CONVERSATION.PERSONAL_PREFERENCES.DISABLE_QUIET'
          : 'CONVERSATION.PERSONAL_PREFERENCES.ENABLE_QUIET'
      ),
      action: 'toggle_personal_quiet',
      value: 'toggle_personal_quiet',
    });
  }

  if (!isRescueConversation.value || isTeamQuiet.value) {
    items.push({
      icon: isTeamQuiet.value ? 'i-lucide-users' : 'i-lucide-users-round',
      label: t(
        isTeamQuiet.value
          ? 'CONVERSATION.PERSONAL_PREFERENCES.DISABLE_TEAM_QUIET'
          : 'CONVERSATION.PERSONAL_PREFERENCES.ENABLE_TEAM_QUIET'
      ),
      action: 'toggle_team_quiet',
      value: 'toggle_team_quiet',
    });
  }

  if (!isRescueConversation.value && !currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-ban',
      label: t('CONVERSATION.PERSONAL_PREFERENCES.BLOCK_CONTACT_GLOBAL'),
      action: 'mute',
      value: 'mute',
    });
  } else if (currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-circle-check',
      label: t('CONVERSATION.PERSONAL_PREFERENCES.UNBLOCK_CONTACT_GLOBAL'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  if (currentChat.value.meta?.hmac_verified) {
    items.push({
      icon: 'i-lucide-merge',
      label: t('CONVERSATION.MERGE.TITLE'),
      action: 'merge_conversation',
      value: 'merge_conversation',
    });
  }
  return items;
});

const handleActionClick = async ({ action }) => {
  toggleDropdown(false);

  if (action === 'toggle_personal_pin') {
    const updated = await togglePinned();
    if (!updated) return;
    useAlert(
      t(
        isPinned.value
          ? 'CONVERSATION.PERSONAL_PREFERENCES.PINNED_SUCCESS'
          : 'CONVERSATION.PERSONAL_PREFERENCES.UNPINNED_SUCCESS'
      )
    );
  } else if (action === 'toggle_personal_quiet') {
    toggleQuiet();
    useAlert(
      t(
        isQuiet.value
          ? 'CONVERSATION.PERSONAL_PREFERENCES.QUIET_ENABLED_SUCCESS'
          : 'CONVERSATION.PERSONAL_PREFERENCES.QUIET_DISABLED_SUCCESS'
      )
    );
  } else if (action === 'toggle_team_quiet') {
    const actionName = isTeamQuiet.value
      ? 'teamUnquietConversation'
      : 'teamQuietConversation';
    await store.dispatch(actionName, currentChat.value.id);
    useAlert(
      t(
        isTeamQuiet.value
          ? 'CONVERSATION.PERSONAL_PREFERENCES.TEAM_QUIET_ENABLED_SUCCESS'
          : 'CONVERSATION.PERSONAL_PREFERENCES.TEAM_QUIET_DISABLED_SUCCESS'
      )
    );
  } else if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  } else if (action === 'merge_conversation') {
    toggleMergeModal(true);
  }
};

// These functions are needed for the event listeners
const mute = () => {
  if (isRescueConversation.value) {
    useAlert(t('CONVERSATION.PERSONAL_PREFERENCES.RESCUE_QUIET_FORBIDDEN'));
    return;
  }

  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
    <MergeConversationModal
      v-if="showMergeModal"
      :conversation="currentChat"
      @close="toggleMergeModal(false)"
    />
  </div>
</template>
